// src/hooks/useExamGuard.js
import { useEffect, useMemo, useRef, useState } from 'react';

const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

export function useExamGuard({ attemptId, enable = true } = {}) {
    const [needsFullscreen, setNeedsFullscreen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);

    // odszumianie – minimalny odstęp między flagami danego typu
    const lastFlagTsRef = useRef({});
    // tłumienie lokalnych blurów (np. przełączanie pytań w UI)
    const uiSuppressRef = useRef(0);
    // zapamiętujemy, kiedy był ostatni fullscreenchange, by ignorować artefakty blur zaraz po
    const lastFsChangeRef = useRef(0);

    const sendFlag = async (type, meta) => {
        if (!attemptId) return;
        const now = Date.now();
        const last = lastFlagTsRef.current[type] || 0;
        if (now - last < 1200) return; // rate-limit per typ
        lastFlagTsRef.current[type] = now;

        try {
            await fetch(`/api/exam/attempts/${attemptId}/flags`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ type, clientTs: now, meta: meta || null }),
            });
        } catch {
            // best effort
        }
    };

    // publiczne API: oznacz „akcję UI” – tłumimy blur przez podany czas (domyślnie 1200 ms)
    const markUiAction = (ms = 1200) => {
        uiSuppressRef.current = Date.now() + ms;
    };

    const enterFullscreen = async () => {
        try {
            const el = document.documentElement;
            if (!document.fullscreenElement) await el.requestFullscreen?.();
            setNeedsFullscreen(false);
        } catch {
            setNeedsFullscreen(true);
        }
    };

    useEffect(() => {
        if (!enable) return;

        // spróbuj wejść w fullscreen na starcie
        if (!document.fullscreenElement) enterFullscreen();

        const onFsChange = () => {
            lastFsChangeRef.current = Date.now();
            const fs = !!document.fullscreenElement;
            setIsFullscreen(fs);
            if (!fs) {
                setNeedsFullscreen(true);
                sendFlag('fullscreen_exit', { reason: 'user_left_fullscreen' });
            }
        };

        // Natychmiastowe, proste wykrycie utraty fokusa okna:
        // - jeśli włączone tłumienie (akcja UI), nie wysyłamy
        // - jeśli to artefakt tuż po fullscreenchange (<600 ms), nie wysyłamy
        // - wysyłamy tylko gdy karta jest widoczna i rzeczywiście brak fokusa
        const onBlur = () => {
            const now = Date.now();
            if (now <= uiSuppressRef.current) return;                 // tłumienie z akcji UI
            if (now - lastFsChangeRef.current < 600) return;          // artefakt po fullscreenchange – ignoruj

            const visible = document.visibilityState === 'visible';
            const hasFocus = document.hasFocus();
            if (visible && !hasFocus) {
                sendFlag('tab_blur', {
                    visibility: document.visibilityState,
                    hasFocus,
                    fullscreen: !!document.fullscreenElement,
                });
            }
        };

        // Karta przestaje być widoczna (inna zakładka/okno) – flaguj od razu
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                sendFlag('visibility_hidden', { fullscreen: !!document.fullscreenElement });
            }
        };

        // Blokady skrótów i akcji (drukowanie, devtools, kopiuj/wklej itd.)
        const blockAndFlag = (ev, type, meta) => {
            ev.preventDefault();
            ev.stopPropagation();
            sendFlag(type, meta);
        };

        const onKeyDown = (ev) => {
            const key = ev.key;
            const ctrl = ev.ctrlKey || ev.metaKey;
            const shift = ev.shiftKey;

            // PrintScreen – sygnalizujemy próbę
            if (key === 'PrintScreen') {
                blockAndFlag(ev, 'printscreen_attempt', { key });
                // próba wyczyszczenia schowka (może być zablokowane przez przeglądarkę)
                navigator.clipboard?.writeText?.(' ').catch(() => {});
                return;
            }

            // Drukowanie
            if (ctrl && (key === 'p' || key === 'P')) {
                blockAndFlag(ev, 'print_attempt', { combo: 'Ctrl/Cmd+P' });
                return;
            }

            // DevTools / View Source
            if (
                key === 'F12' ||
                (ctrl && shift && (key === 'I' || key === 'i' || key === 'J' || key === 'j')) ||
                (ctrl && (key === 'U' || key === 'u'))
            ) {
                blockAndFlag(ev, 'shortcut_blocked', { key, combo: 'devtools_or_view_source' });
                return;
            }

            // Copy / Cut / Paste / Select All / Save
            if (ctrl && ['c','C','x','X','v','V','a','A','s','S'].includes(key)) {
                const map = { c:'copy', C:'copy', x:'cut', X:'cut', v:'paste', V:'paste', a:'select_all', A:'select_all', s:'save', S:'save' };
                blockAndFlag(ev, `${map[key] || 'shortcut_blocked'}_attempt`, { key });
                return;
            }
        };

        const onCopy  = (ev) => blockAndFlag(ev, 'copy_attempt');
        const onCut   = (ev) => blockAndFlag(ev, 'cut_attempt');
        const onPaste = (ev) => blockAndFlag(ev, 'paste_attempt');

        const onCtx   = (ev) => blockAndFlag(ev, 'contextmenu');
        const onDrag  = (ev) => blockAndFlag(ev, 'drag');

        document.addEventListener('fullscreenchange', onFsChange, true);
        document.addEventListener('visibilitychange', onVisibility, true);
        window.addEventListener('blur', onBlur, false); // ważne: bez capture

        document.addEventListener('keydown', onKeyDown, true);
        document.addEventListener('copy', onCopy, true);
        document.addEventListener('cut', onCut, true);
        document.addEventListener('paste', onPaste, true);
        document.addEventListener('contextmenu', onCtx, true);
        document.addEventListener('dragstart', onDrag, true);

        return () => {
            document.removeEventListener('fullscreenchange', onFsChange, true);
            document.removeEventListener('visibilitychange', onVisibility, true);
            window.removeEventListener('blur', onBlur, false);

            document.removeEventListener('keydown', onKeyDown, true);
            document.removeEventListener('copy', onCopy, true);
            document.removeEventListener('cut', onCut, true);
            document.removeEventListener('paste', onPaste, true);
            document.removeEventListener('contextmenu', onCtx, true);
            document.removeEventListener('dragstart', onDrag, true);
        };
    }, [attemptId, enable]); // eslint-disable-line react-hooks/exhaustive-deps

    return useMemo(() => ({
        needsFullscreen,
        isFullscreen,
        enterFullscreen,
        markUiAction, // ← wywołuj przed zmianą pytania, żeby stłumić lokalny blur
    }), [needsFullscreen, isFullscreen]);
}
